import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Select, Switch, Divider } from 'antd';
import { apiUpdateUser } from '@services/ManageUserOperator/UserOperatorServices';
import { apiGetAllRoles } from '@services/ManageRoles/RoleServices';
import { showSuccess, showError } from '@components/notification';
import { usePermission } from '@contexts/Auth/UsePermission';
import BranchSelectMultiple from '@components/FilterAdvance/components/SelectBranchMultiple';
import ManageGroupSelect from '@components/FilterAdvance/components/SelectManageGroup';

const { Option } = Select;

const EditUserModal = ({ open, onClose, userInfo, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const { isSuperAdmin } = usePermission();

  // Fetch roles khi modal mở
  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const response = await apiGetAllRoles();
      if (response?.data?.data) {
        let roleData = response.data.data;

        // Chỉ SUPER_ADMIN mới có thể gán role SUPER_ADMIN
        if (!isSuperAdmin) {
          roleData = roleData.filter((role) => role.name !== 'SUPER_ADMIN');
        }

        setRoles(roleData);
      }
    } catch (error) {
      console.error('Lỗi khi tải roles:', error);
      showError('Không thể tải danh sách vai trò');
    } finally {
      setLoadingRoles(false);
    }
  };

  // Set form data từ userInfo
  const setFormData = (user) => {
    try {
      setLoadingUserData(true);

      // Lấy role ID đầu tiên từ userInfo.roleDetails
      const roleId = user?.roleDetails?.[0]?.id || undefined;

      form.setFieldsValue({
        user_name: user.user_name,
        role_ids: roleId,
        is_company_user: user.is_company_user || false,
        branch_code: user.branch_code || [],
        group_code: user.group_code || []
      });
    } catch (error) {
      console.error('Lỗi khi set form data:', error);
      showError('Không thể tải thông tin người dùng');
    } finally {
      setLoadingUserData(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchRoles();
      if (userInfo) {
        setFormData(userInfo);
      }
    } else {
      form.resetFields();
    }
  }, [open, userInfo, form]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Nếu không nhập password thì bỏ qua field này
      const submitData = { ...values };
      if (!submitData.password) {
        delete submitData.password;
      }

      // Convert role_ids to array for backend
      if (submitData.role_ids) {
        submitData.role_ids = [submitData.role_ids];
      }

      // Nếu LÀ company user thì xóa branch/group data
      if (submitData.is_company_user) {
        delete submitData.branch_code;
        delete submitData.group_code;
      }

      await apiUpdateUser(userInfo.id, submitData);
      showSuccess({ message: 'Cập nhật người dùng thành công!' });

      form.resetFields();
      onClose();
      onSuccess();
    } catch (error) {
      console.log('Có lỗi: ', error?.message);
      showError(error?.message || 'Cập nhật người dùng thất bại');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Chỉnh sửa thông tin người dùng"
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      destroyOnClose
      confirmLoading={loadingUserData}>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label={
            <span>
              Tên đăng nhập <span style={{ color: 'red' }}>*</span>
            </span>
          }
          name="user_name"
          rules={[
            { required: true, message: 'Vui lòng nhập tên đăng nhập!' },
            { min: 3, message: 'Tên đăng nhập phải có ít nhất 3 ký tự!' },
            { max: 50, message: 'Tên đăng nhập không được vượt quá 50 ký tự!' }
          ]}>
          <Input placeholder="Nhập tên đăng nhập" />
        </Form.Item>
        <Form.Item
          label="Mật khẩu mới"
          name="password"
          help="Để trống nếu không muốn thay đổi mật khẩu"
          rules={[{ min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự!' }]}>
          <Input.Password placeholder="Nhập mật khẩu mới (tùy chọn)" />
        </Form.Item>
        <Form.Item
          label={
            <span>
              Vai trò <span style={{ color: 'red' }}>*</span>
            </span>
          }
          name="role_ids"
          rules={[{ required: true, message: 'Vui lòng chọn vai trò!' }]}>
          <Select placeholder="Chọn vai trò cho người dùng" loading={loadingRoles} allowClear>
            {roles.map((role) => (
              <Option key={role.id} value={role.id}>
                <div>
                  <div style={{ fontWeight: 500 }}>{role.name}</div>
                  <div style={{ fontSize: '12px', color: '#888' }}>{role.description}</div>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Divider>Vùng quản lý</Divider>
        <Form.Item label="Tài khoản cấp công ty" name="is_company_user" valuePropName="checked">
          <Switch
            checkedChildren="Có"
            unCheckedChildren="Không"
            onChange={(checked) => {
              // Clear branch and group when turning ON company user (không cần nhập)
              if (checked) {
                form.setFieldsValue({
                  branch_code: [],
                  group_code: []
                });
              }
            }}
          />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) => prev.is_company_user !== curr.is_company_user}>
          {({ getFieldValue }) =>
            !getFieldValue('is_company_user') && (
              <>
                <Form.Item
                  label={
                    <span>
                      Nhà máy <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  name="branch_code"
                  rules={[
                    { required: true, message: 'Vui lòng chọn ít nhất một Nhà máy!' },
                    { type: 'array', min: 1, message: 'Vui lòng chọn ít nhất một Nhà máy!' }
                  ]}>
                  <BranchSelectMultiple
                    mode="multiple"
                    showTitle={false}
                    onChange={(value) => {
                      form.setFieldsValue({ branch_code: value });
                      // Reset group when branch changes
                      form.setFieldsValue({ group_code: [] });
                    }}
                  />
                </Form.Item>

                <Form.Item label="Tổ quản lý" name="group_code">
                  <ManageGroupSelect
                    key={JSON.stringify(getFieldValue('branch_code'))}
                    mode="multiple"
                    showTitle={false}
                    parentBranch={getFieldValue('branch_code')}
                    onChange={(value) => {
                      form.setFieldsValue({ group_code: value });
                    }}
                  />
                </Form.Item>
              </>
            )
          }
        </Form.Item>{' '}
        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Hủy
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ backgroundColor: '#0051A9', borderColor: '#0051A9' }}>
            Cập nhật
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditUserModal;
